import { useState, useRef, useLayoutEffect } from 'react';

import {
  BpkButtonV2,
  BUTTON_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import ChevronDown from '@skyscanner/backpack-web/bpk-component-icon/sm/chevron-down';
import ChevronUp from '@skyscanner/backpack-web/bpk-component-icon/sm/chevron-up';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';

import type { HotelDescription as HotelDescriptionType } from '@shared/types/hotel-content';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './DescriptionContent.module.scss';

import useTranslation from '@hotels-hotel-detail/data-access/stores/useTranslation';
import TranslationButton from '@shared/ui/components/Translation/TranslationButton';
import TranslationContent from '@shared/ui/components/Translation/TranslationContent';
import { withI18n } from '@shared/ui/contexts/i18n';
import { TranslationState } from '@shared/utils-frontend/translate-state';

const INITIAL_LINE_CLAMP_MOBILE = 4;
const INITIAL_LINE_CLAMP_DESKTOP = 5;
const TERMINAL_LINE_CLAMP = 999;

type Props = {
  hotelDescription: HotelDescriptionType;
  i18n: I18nService;
  isMobile: boolean;
};

type TranslationResponse = {
  content: string;
};

const DescriptionContent = ({ hotelDescription, i18n, isMobile }: Props) => {
  const [showMoreButton, setShowMoreButton] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const { content, locale, translated } = hotelDescription;
  const {
    isPending,
    state: translationState,
    translate,
    translatedData,
  } = useTranslation({
    contentType: 'hotel-description',
    sourceLocale: locale,
    targetLocale: i18n.culture.locale,
    data: { content },
    excludedFields: ['hotel-description'],
  });

  const contentTextRef = useRef<HTMLParagraphElement>(null);

  const scrollToTop = () => {
    const anchorElement = document.getElementById('hotel-description');
    if (expanded && anchorElement) {
      anchorElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleHeight = () => {
    const contentTextElement = contentTextRef.current;

    if (contentTextElement) {
      const lineClamp = parseInt(contentTextElement.style.webkitLineClamp, 10);
      if (lineClamp === TERMINAL_LINE_CLAMP) {
        setExpanded(false);
        contentTextElement.style.webkitLineClamp = `${isMobile ? INITIAL_LINE_CLAMP_MOBILE : INITIAL_LINE_CLAMP_DESKTOP}`;
      } else {
        setExpanded(true);
        contentTextElement.style.webkitLineClamp = `${TERMINAL_LINE_CLAMP}`;
      }
    }
    requestAnimationFrame(() => {
      isMobile && scrollToTop();
    });
  };

  let renderDescription: TranslationResponse | HotelDescriptionType =
    hotelDescription;
  if (translationState === TranslationState.TRANSLATED) {
    renderDescription = translatedData as TranslationResponse;
  }

  // NOTE: First space is half-space so need to change to full-space, can't use replace directly
  if (
    renderDescription &&
    renderDescription.content &&
    renderDescription.content.indexOf(' ') !== -1
  ) {
    renderDescription.content = renderDescription.content.split(' ').join(' ');
  }

  useLayoutEffect(() => {
    const contentTextElement = contentTextRef.current;
    if (contentTextElement) {
      requestAnimationFrame(() => {
        if (contentTextElement.scrollHeight > contentTextElement.clientHeight) {
          setShowMoreButton(true);
        }
      });
    }
  }, [renderDescription.content]);

  const ChevronIcon = expanded ? ChevronUp : ChevronDown;

  return (
    <TranslationContent isPending={isPending}>
      <div className={STYLES.DescriptionContent}>
        <p
          ref={contentTextRef}
          className={STYLES.DescriptionContent__text}
          dangerouslySetInnerHTML={{ __html: `${renderDescription.content}` }} // eslint-disable-line react/no-danger
        />
        {!translated && (
          <div className={STYLES.DescriptionContent__translateButton}>
            <TranslationButton
              onTranslate={translate}
              translationState={translationState}
            />
          </div>
        )}
        {showMoreButton && (
          <div className={STYLES.DescriptionContent__seeMoreButton}>
            <BpkButtonV2
              type={isMobile ? BUTTON_TYPES.link : BUTTON_TYPES.secondary}
              onClick={toggleHeight}
              className={STYLES.DescriptionContent__textAndIcon}
            >
              <BpkText
                textStyle={TEXT_STYLES.label1}
                className={STYLES.DescriptionContent__btnText}
              >
                {i18n.translate(
                  expanded ? 'Button_viewLess' : 'Button_viewMore',
                )}
              </BpkText>
              {isMobile && <ChevronIcon />}
            </BpkButtonV2>
          </div>
        )}
      </div>
    </TranslationContent>
  );
};

export default withI18n(DescriptionContent);
